import { Schemas } from '@api-client/generated/types';

type AccessRights = Schemas.AccessRights;
type AdminRole = NonNullable<Schemas.User['adminRole']>;

/**
 * The six configurable client-portal access rights, in display order, with the
 * human-readable labels shown in the UI. The internal keys (including the
 * backend spelling `incomesExpences`) never surface to the user.
 */
export const ACCESS_RIGHTS: ReadonlyArray<{
  key: keyof AccessRights;
  label: string;
}> = [
  { key: 'transactions', label: 'Transactions' },
  { key: 'companyData', label: 'Company data' },
  { key: 'incomesExpences', label: 'Income & expenses' },
  { key: 'reports', label: 'Reports' },
  { key: 'payroll', label: 'Payroll' },
  { key: 'userManagement', label: 'User management' },
];

const allRights = (value: boolean): AccessRights => ({
  transactions: value,
  companyData: value,
  userManagement: value,
  reports: value,
  payroll: value,
  incomesExpences: value,
});

/**
 * Default access rights pre-filled when attaching a team member, keyed by their
 * backoffice role. Admins can override every value before confirming.
 */
export const ROLE_ACCESS_DEFAULTS: Record<AdminRole, AccessRights> = {
  admin: allRights(true),
  accountant: {
    transactions: true,
    companyData: true,
    incomesExpences: true,
    reports: true,
    payroll: false,
    userManagement: false,
  },
  kyc: { ...allRights(false), companyData: true },
  support: { ...allRights(false), companyData: true },
};

export const emptyAccessRights = (): AccessRights => allRights(false);

export const countGrantedRights = (rights: AccessRights): number =>
  ACCESS_RIGHTS.filter(({ key }) => rights[key]).length;

type RightLabel = { key: keyof AccessRights; label: string };

/**
 * Normalized before/after diff of two access-right sets (nulls treated as no
 * access), for rendering audit-journal entries. `added` = newly granted,
 * `removed` = revoked.
 */
export const diffAccessRights = (
  before: AccessRights | null,
  after: AccessRights | null
): { added: RightLabel[]; removed: RightLabel[] } => {
  const from = before ?? emptyAccessRights();
  const to = after ?? emptyAccessRights();
  return {
    added: ACCESS_RIGHTS.filter(({ key }) => !from[key] && to[key]),
    removed: ACCESS_RIGHTS.filter(({ key }) => from[key] && !to[key]),
  };
};
